import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useEffect, useRef } from "react";
import { useDateRange } from "../../common/components/datePicker/modelsDatePickers";
import { useCustomerIdGuard } from "../../common/hooks/useCustomerIdGuard";
import { useGetCurrentUser } from "../../common/hooks/useGetUser";
import { useSelectedLocation } from "../../common/variables/selectedLocation";
import { useSelectedModel } from "../../common/variables/selectedModel";
import { DEFAULT_CONTROL_ROOM_REFRESH_MINUTES } from "../../leak-finder/hooks/useLazyGetUser";
import EventPageTitle from "./components/EventPageTitle";
import RecentActivitiesFilters from "./components/RecentActivitiesFilters";
import EventTableContainer from "./components/table/EventTableContainer";

const RecentActivitiesPage = () => {
  const customerId = useCustomerIdGuard();
  const modelId = useSelectedModel();
  const facilityId = useSelectedLocation();
  const dateRange = useDateRange();
  const { userInfo } = useGetCurrentUser();

  const refreshTimerRef = useRef<number | null>(null);

  // Legacy users (undefined/null) get 2-minute refresh (current behavior)
  // Only explicitly setting to 0 disables refresh
  const refreshMinutes =
    userInfo?.userSettings?.controlRoomRefreshMinutes ??
    DEFAULT_CONTROL_ROOM_REFRESH_MINUTES;

  useEffect(() => {
    // Only disable if explicitly set to 0
    if (refreshMinutes === 0) {
      return;
    }

    const start = () => {
      if (refreshTimerRef.current) return;

      refreshTimerRef.current = window.setInterval(() => {
        const isMediaOpen = document.querySelector(
          ".MuiDialog-root, .MuiModal-root, video[controls]"
        );

        if (!isMediaOpen) {
          window.location.reload();
        }
      }, refreshMinutes * 60 * 1000);
    };

    const stop = () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);

        refreshTimerRef.current = null;
      }
    };

    start();

    return () => {
      stop();
    };
  }, [refreshMinutes]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1em",
      }}
    >
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <EventPageTitle title="Control room" />
        </Grid>

        <RecentActivitiesFilters />
      </Grid>

      <EventTableContainer
        key={`${customerId}-${modelId?.value ?? ""}-${facilityId?.value ?? ""}`}
        customerId={customerId}
        modelId={modelId}
        facilityId={facilityId}
        dateRange={dateRange}
      />
    </Box>
  );
};

export default RecentActivitiesPage;
